/** @jsxImportSource theme-ui */
import { Badge, Box, Checkbox, Flex, Heading, Label, Link, Text } from 'theme-ui';
import { useState } from 'react';
import { format } from 'date-fns';
import { useGetCompanyDeletionSchedulesQuery } from '@/store/apiService';
import DataTable from '@/components/DataTable';
import { Container } from '@/components/styled';
import { COMPANY_DELETION_SCHEDULE_STATUS, CompanyDeletionScheduleResponse } from '@/types/types';
import { CancelDeletionScheduleButton } from './CancelDeletionScheduleButton';
import { licenseActionLabel, sourceLabel, statusLabel } from './labels';

type Row = { original: CompanyDeletionScheduleResponse };

const formatDate = (value?: string | null, withTime = false) =>
    value ? format(new Date(value), withTime ? 'd MMM yyyy, HH:mm' : 'd MMM yyyy') : '';

const CompanyCell = ({ row }: { row: Row }) => {
    const schedule = row.original;
    return (
        <Box>
            {schedule.CompanyExists ? (
                <Link href={`/#/clients/${schedule.CompanyId}`}>
                    {schedule.CompanyName ?? schedule.CompanyId}
                </Link>
            ) : (
                <Text>{schedule.CompanyName ?? schedule.CompanyId}</Text>
            )}
            <Text as="p" sx={{ fontSize: 0, color: 'gray.6' }}>
                {schedule.CompanyId}
            </Text>
            {!schedule.CompanyExists ? (
                <Text as="p" sx={{ fontSize: 0 }} color="danger">
                    deleted
                </Text>
            ) : null}
        </Box>
    );
};

const StatusCell = ({ row }: { row: Row }) => {
    const status = statusLabel(row.original.Status);
    return <Badge backgroundColor={status.color}>{status.label}</Badge>;
};

const VossCell = ({ row }: { row: Row }) => {
    const schedule = row.original;
    if (!schedule.VossCustomerId && !schedule.VossCustomerNote) {
        return null;
    }
    return (
        <Box>
            {schedule.VossCustomerId ? (
                <Flex sx={{ gap: 1, alignItems: 'center', justifyContent: 'center' }}>
                    <Text sx={{ fontSize: 0 }}>{schedule.VossCustomerId}</Text>
                    <Link
                        href="#"
                        title="Copy the Voss customer id"
                        onClick={(evt) => {
                            evt.preventDefault();
                            navigator.clipboard?.writeText(schedule.VossCustomerId ?? '');
                        }}
                    >
                        copy
                    </Link>
                </Flex>
            ) : null}
            {schedule.VossCustomerNote ? (
                <Text as="p" sx={{ fontSize: 0 }}>
                    {schedule.VossCustomerNote}
                </Text>
            ) : null}
        </Box>
    );
};

const WarningsCell = ({ row }: { row: Row }) => {
    const schedule = row.original;
    const lines = [
        ...(schedule.Warnings ?? []),
        ...(schedule.ErrorMessage ? [schedule.ErrorMessage] : []),
    ];
    return (
        <Box>
            {lines.map((line) => (
                <Text as="p" key={line} sx={{ fontSize: 0 }} color="danger">
                    {line}
                </Text>
            ))}
        </Box>
    );
};

const ActionsCell = ({ row }: { row: Row }) =>
    row.original.Status === COMPANY_DELETION_SCHEDULE_STATUS.Scheduled &&
    row.original.CompanyExists ? (
        <Flex sx={{ justifyContent: 'flex-end', flexGrow: 1 }}>
            <CancelDeletionScheduleButton companyId={row.original.CompanyId} />
        </Flex>
    ) : null;

const columns = [
    {
        columnKey: 'Company',
        accessor: 'CompanyName',
        Header: 'Company',
        Cell: CompanyCell,
        width: '22%',
    },
    { columnKey: 'Status', accessor: 'Status', Header: 'Status', Cell: StatusCell, width: '90px' },
    {
        columnKey: 'SourceName',
        accessor: 'SourceName',
        Header: 'Source',
        Cell: ({ row }: { row: Row }) => <>{sourceLabel(row.original.SourceName)}</>,
        width: '120px',
    },
    {
        columnKey: 'ScheduledDeletionDate',
        accessor: 'ScheduledDeletionDate',
        Header: 'Deletion date',
        Cell: ({ row }: { row: Row }) => <>{formatDate(row.original.ScheduledDeletionDate)}</>,
        width: '110px',
    },
    {
        columnKey: 'Reason',
        accessor: 'Reason',
        Header: 'Reason',
        Cell: ({ row }: { row: Row }) => (
            <Box>
                <Text as="p">{row.original.Reason}</Text>
                <Text as="p" sx={{ fontSize: 0, color: 'gray.6' }}>
                    {licenseActionLabel(row.original.MetaData?.LicenseTerminationTiming)}
                </Text>
                {row.original.CancelReason ? (
                    <Text as="p" sx={{ fontSize: 0, color: 'gray.6' }}>
                        Cancelled: {row.original.CancelReason}
                    </Text>
                ) : null}
            </Box>
        ),
        width: '20%',
    },
    {
        columnKey: 'CreatedBy',
        accessor: 'CreatedBy',
        Header: 'Created',
        Cell: ({ row }: { row: Row }) => (
            <Box>
                <Text as="p">{row.original.CreatedBy}</Text>
                <Text as="p" sx={{ fontSize: 0, color: 'gray.6' }}>
                    {formatDate(row.original.CreatedDate, true)}
                </Text>
            </Box>
        ),
        width: '150px',
    },
    {
        columnKey: 'VossCustomer',
        accessor: 'VossCustomerId',
        Header: 'Voss customer',
        Cell: VossCell,
    },
    { columnKey: 'Warnings', accessor: 'Warnings', Header: 'Warnings', Cell: WarningsCell },
    { columnKey: 'Actions', accessor: 'Actions', Header: '', Cell: ActionsCell },
];

const CompanyDeletionSchedules = () => {
    const [showProcessed, setShowProcessed] = useState(false);
    const schedules = useGetCompanyDeletionSchedulesQuery(
        showProcessed ? {} : { Statuses: [COMPANY_DELETION_SCHEDULE_STATUS.Scheduled] }
    );

    return (
        <Container
            sx={{
                table: {
                    whiteSpace: 'normal',
                },
                td: {
                    textAlign: 'center',
                },
                'td > div': {
                    margin: '0 auto',
                },
            }}
        >
            <Flex sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Heading>Scheduled deletions</Heading>
                <Label sx={{ width: 'auto', alignItems: 'center' }}>
                    <Checkbox
                        checked={showProcessed}
                        onChange={(e) => setShowProcessed(e.target.checked)}
                    />
                    <Text>Show processed</Text>
                </Label>
            </Flex>
            <DataTable
                data={schedules?.data?.Results}
                columns={columns}
                isLoading={schedules.isLoading}
                isFetching={schedules.isFetching}
            />
        </Container>
    );
};

export default CompanyDeletionSchedules;
